/**
 * Old-wallet residuals and continuity (the inflows cohort, AD-1c): pure selectors behind Wallet 360's
 * Previous wallets panel and the Old-wallet residuals page.
 *
 * Money keeps reaching a wallet after its migration (payroll through the old bank details, a relay
 * deposit to an old receive address, a card refund, a saved address). wallet-service 0.78.0 forwards
 * each credit to the successor's same-role account with the migration's standing forward keys
 * (`migrate-{main,reserve}-forward`, live 90 days after completion), then only the owner's one-tap
 * Move (a one-shot `residual-{role}-move` key) moves it.
 *
 * Mirrors elios-backend `wallet-migration/forward-authority.ts` (`forwardKeyIdOf`,
 * `forwardKeysExpireAt`, the 5-minute dispatch margin), `residual-forward/service.ts`
 * (`resolveForwardKey`: what "live" means), `residual-forward/policy.ts` (the 0.10 USDC floor, the
 * 30-minute overdue page) and `residual-forward/admin-api.controller.ts` (Forward now and the Move
 * re-prompt). The backend re-checks everything; these only decide what to show and offer.
 */
import type { MantineColor } from "@mantine/core";
import type {
  ResidualForwardDoc,
  SessionKeyDoc,
  WalletAvailabilityDoc,
  WalletDoc,
  WalletKeyInventoryDoc,
  WalletMigrationDoc,
  WithDocId,
} from "@/firestore/types";
import { uuidv5 } from "@/lib/uuidv5";
import type { AlertMarker, CommandAvailability } from "./migrations";

const MINUTE_MS = 60_000;
const DAY_MS = 24 * 60 * MINUTE_MS;

/** `RESIDUAL_FORWARD_MIN_MICRO_USDC`: automatic forwards skip a balance below 0.10 USDC. */
export const RESIDUAL_FLOOR_MICRO_USDC = 100_000;
/** `FORWARD_KEY_LIFETIME_MS`: forward keys forward for 90 days after the migration completed. */
export const FORWARD_KEY_LIFETIME_MS = 90 * DAY_MS;
/** `RESIDUAL_FORWARD_DISPATCH_MARGIN_MS`: a key is not used within 5 minutes of its `validUntil`. */
export const RESIDUAL_FORWARD_DISPATCH_MARGIN_MS = 5 * MINUTE_MS;
/** `RESIDUAL_FORWARD_OVERDUE_MS`: a forward in flight this long after its last change pages `overdue`. */
export const RESIDUAL_FORWARD_OVERDUE_MS = 30 * MINUTE_MS;

export type AccountRole = "main" | "reserve";
export const ACCOUNT_ROLES: readonly AccountRole[] = ["main", "reserve"];

export const FORWARD_KEY_TYPE: Readonly<Record<AccountRole, string>> = {
  main: "migrate-main-forward",
  reserve: "migrate-reserve-forward",
};
export const MOVE_KEY_TYPE: Readonly<Record<AccountRole, string>> = {
  main: "residual-main-move",
  reserve: "residual-reserve-move",
};
/** The forward keys' purposes in a migration's `approvalSessionKeys`. */
export const FORWARD_KEY_PURPOSE: Readonly<Record<AccountRole, string>> = {
  main: "forwardMain",
  reserve: "forwardReserve",
};
const FORWARD_KEY_TYPES: ReadonlySet<string> = new Set(Object.values(FORWARD_KEY_TYPE));

/** Wallet states the backend forwards from (`PREVIOUS_WALLET_STATES`). */
export const PREVIOUS_WALLET_STATES = ["migrated", "retired"] as const;

export function isPreviousWallet(wallet: Pick<WalletDoc, "state"> | null | undefined): boolean {
  return !!wallet && (PREVIOUS_WALLET_STATES as readonly string[]).includes(wallet.state);
}

function ms(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function iso(value: number | null): string | null {
  return value === null ? null : new Date(value).toISOString();
}

type MigrationLike = Pick<WalletMigrationDoc, "oldWallet" | "state" | "createdAt"> &
  Partial<Pick<WalletMigrationDoc, "id" | "newWallet" | "completedAt" | "approvalSessionKeys">>;

function newestFirst<T extends Pick<WalletMigrationDoc, "createdAt">>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => (ms(b.createdAt) ?? 0) - (ms(a.createdAt) ?? 0));
}

/**
 * The migration that left `walletId` behind: its newest `completed` / `cleanedUp` migration as the
 * backend reads it (`findCompletedMigrationOfSource`), else its newest migration of any state.
 */
export function migrationOfSource<T extends MigrationLike>(
  migrations: readonly T[],
  walletId: string,
): T | null {
  const own = newestFirst(migrations.filter((m) => m.oldWallet === walletId));
  return own.find((m) => m.state === "completed" || m.state === "cleanedUp") ?? own[0] ?? null;
}

/** `forwardKeysExpireAt`: `completedAt` + 90 days, or null while the migration has not completed. */
export function forwardKeysExpireAt(
  migration: Partial<Pick<WalletMigrationDoc, "completedAt">> | null | undefined,
): string | null {
  const completed = ms(migration?.completedAt);
  return completed === null ? null : iso(completed + FORWARD_KEY_LIFETIME_MS);
}

// ───────────────────────────── forward keys ─────────────────────────────

export type ForwardKeyStatus = "signed" | "pending" | "expired" | "revoked" | "missing";

const STATUS_DISPLAY: Record<ForwardKeyStatus, { label: string; color: MantineColor }> = {
  signed: { label: "Signed", color: "green" },
  pending: { label: "Pending (not signed)", color: "yellow" },
  expired: { label: "Expired", color: "gray" },
  revoked: { label: "Revoked early", color: "orange" },
  missing: { label: "Missing", color: "gray" },
};

export interface ForwardKeyState {
  role: AccountRole;
  status: ForwardKeyStatus;
  label: string;
  color: MantineColor;
  /** The key's id (from the migration's batch, else the newest key of the type on the wallet). */
  sessionKeyId: string | null;
  key: KeyLike | null;
  /** When it stops forwarding: `completedAt` + 90 days, capped by its `validUntil`. */
  expiresAt: string | null;
  /** Usable for a forward now (what `POST /admin/residualForwards` needs). */
  live: boolean;
  note: string;
}

export type KeyLike = Pick<SessionKeyDoc, "account" | "type" | "state"> &
  Partial<Pick<SessionKeyDoc, "id" | "validUntil" | "recipientAddress" | "createdAt">> & {
    _docId?: string;
  };

function keyId(key: KeyLike): string {
  return key.id ?? key._docId ?? "";
}

function withStatus(
  status: ForwardKeyStatus,
  rest: Omit<ForwardKeyState, "status" | "label" | "color">,
): ForwardKeyState {
  return { status, ...STATUS_DISPLAY[status], ...rest };
}

/**
 * One role's forward key on a previous wallet, as `resolveForwardKey` would judge it now: missing
 * (no key: a migration before wallet-service 0.78.0, or a retired wallet), pending (the owner never
 * signed it in the batch), signed (enabled: live while within 90 days of completion and 5 minutes
 * before its `validUntil`, from a `migrated` wallet), expired, or revoked before its time (an abandon,
 * or the expiry sweep after a failed migration).
 */
export function forwardKeyState(input: {
  role: AccountRole;
  wallet: Pick<WithDocId<WalletDoc>, "_docId" | "state">;
  migration: MigrationLike | null;
  keys: readonly KeyLike[];
  now: number;
}): ForwardKeyState {
  const { role, wallet, migration, now } = input;
  const own = input.keys.filter((k) => k.account === wallet._docId);
  const batchId =
    migration?.approvalSessionKeys?.find((k) => k.purpose === FORWARD_KEY_PURPOSE[role])
      ?.sessionKeyId ?? null;
  const byType = [...own]
    .filter((k) => k.type === FORWARD_KEY_TYPE[role])
    .sort((a, b) => (ms(b.createdAt) ?? 0) - (ms(a.createdAt) ?? 0))[0];
  const key = batchId ? (own.find((k) => keyId(k) === batchId) ?? null) : (byType ?? null);
  const sessionKeyId = batchId ?? (key ? keyId(key) : null);

  if (!key) {
    const note =
      wallet.state === "retired"
        ? "A retired wallet has no forward keys: only the owner's one-shot Move moves its balance."
        : !migration
          ? "No migration from this wallet found: only the owner's one-shot Move."
          : batchId
            ? "The migration's batch names a forward key the console cannot read."
            : "The migration predates wallet-service 0.78.0 (no forward keys in its batch): late inflows are skipped keyMissing and go through the owner's Move.";
    return withStatus("missing", {
      role,
      sessionKeyId,
      key: null,
      expiresAt: null,
      live: false,
      note,
    });
  }

  const completedExpiry = ms(forwardKeysExpireAt(migration));
  const validUntil = ms(key.validUntil);
  const expiresAtMs =
    completedExpiry !== null && validUntil !== null
      ? Math.min(completedExpiry, validUntil)
      : (completedExpiry ?? validUntil);
  const expiresAt = iso(expiresAtMs);
  const pastExpiry = expiresAtMs !== null && now >= expiresAtMs;
  const base = { role, sessionKeyId, key, expiresAt };

  if (key.state === "revoked") {
    if (pastExpiry)
      return withStatus("expired", {
        ...base,
        live: false,
        note: "Past its 90 days, revoked by the daily expiry: only the owner's one-shot Move.",
      });
    return withStatus("revoked", {
      ...base,
      live: false,
      note:
        completedExpiry !== null
          ? "Revoked before its 90 days were up (the +24 h teardown keeps forward keys: check why). Only the owner's one-shot Move."
          : "Revoked with its migration: an abandon, or the expiry sweep after a failed migration.",
    });
  }
  if (pastExpiry) {
    return withStatus("expired", {
      ...base,
      live: false,
      note: "Past its 90 days: the daily expiry (04:41 UTC) revokes it. Only the owner's one-shot Move.",
    });
  }
  if (key.state === "pending") {
    return withStatus("pending", {
      ...base,
      live: false,
      note: "Never signed in the migration's batch (its prompt lapsed): late inflows are skipped keyPending. Forward keys are never re-prompted: the owner's one-shot Move moves the balance.",
    });
  }
  if (key.state !== "enabled") {
    return withStatus("missing", {
      ...base,
      live: false,
      note: `Unknown key state ${key.state}.`,
    });
  }
  const completed = migration?.state === "completed" || migration?.state === "cleanedUp";
  const useUntil =
    validUntil === null
      ? null
      : Math.min(validUntil - RESIDUAL_FORWARD_DISPATCH_MARGIN_MS, completedExpiry ?? Infinity);
  if (wallet.state !== "migrated" || !completed) {
    return withStatus("signed", {
      ...base,
      live: false,
      note: "Signed, but a forward key is used only from a migrated wallet once its migration completed.",
    });
  }
  if (useUntil === null || now >= useUntil) {
    return withStatus("signed", {
      ...base,
      live: false,
      note: "Signed, but within 5 minutes of its validUntil: no longer dispatched.",
    });
  }
  return withStatus("signed", {
    ...base,
    live: true,
    note: "Live: credits are forwarded to the successor with no owner signature.",
  });
}

export function forwardKeyStates(input: {
  wallet: Pick<WithDocId<WalletDoc>, "_docId" | "state">;
  migration: MigrationLike | null;
  keys: readonly KeyLike[];
  now: number;
}): Record<AccountRole, ForwardKeyState> {
  return {
    main: forwardKeyState({ ...input, role: "main" }),
    reserve: forwardKeyState({ ...input, role: "reserve" }),
  };
}

/** The roles whose forward key is live. */
export function liveForwardRoles(states: Record<AccountRole, ForwardKeyState>): AccountRole[] {
  return ACCOUNT_ROLES.filter((role) => states[role].live);
}

/**
 * Forward keys still pending or enabled on a wallet that is no longer (or never became) a previous
 * wallet: one in state other than `migrating` / `migrated`, or whose migration failed. The expiry
 * sweep revokes them and pages `forwardKeyOnOperableWallet` (P1): the abandon should have.
 */
export function forwardKeysOnOperableWallet(input: {
  wallet: Pick<WithDocId<WalletDoc>, "_docId" | "state">;
  keys: readonly KeyLike[];
  migrations: readonly MigrationLike[];
}): KeyLike[] {
  const { wallet } = input;
  return input.keys.filter((key) => {
    if (key.account !== wallet._docId || !FORWARD_KEY_TYPES.has(key.type)) return false;
    if (key.state !== "pending" && key.state !== "enabled") return false;
    if (wallet.state !== "migrating" && wallet.state !== "migrated") return true;
    const owner = input.migrations.find((m) =>
      m.approvalSessionKeys?.some((entry) => entry.sessionKeyId === keyId(key)),
    );
    return owner?.state === "failed";
  });
}

// ───────────────────────────── forwards ─────────────────────────────

export const RESIDUAL_FORWARD_OPERATION_NAMESPACE = "0e6b9d47-3f12-4c8a-a7e5-91d2c4b8f063";

/**
 * The forward's smart-account operation id (`residualForwardOperationId`): the projection does not
 * carry it, so the console derives it to read the operation's state and transaction. A skipped
 * forward has none.
 */
export function residualForwardOperationId(
  forward: Pick<ResidualForwardDoc, "id" | "state">,
): string | null {
  if (forward.state === "skipped") return null;
  return uuidv5(`residual-forward-operation:${forward.id}`, RESIDUAL_FORWARD_OPERATION_NAMESPACE);
}

export const SKIP_REASON_LABELS: Readonly<Record<string, string>> = {
  belowFloor: "Below the 0.10 USDC floor",
  noBalance: "Nothing on the account",
  keyPending: "Forward key never signed",
  keyMissing: "No forward key",
  keyExpired: "Forward key past its 90 days",
  destinationUnavailable: "Recipient no longer the owner's",
  rateLimited: "Rate limited (48 a day)",
  disabled: "Forwarding switched off",
  sourceOperable: "Not a previous wallet",
};

export const TRIGGER_LABELS: Readonly<Record<string, string>> = {
  inflow: "Inflow",
  reconciliation: "Reconciliation sweep",
  migrationCompleted: "Arming at completion",
  keyEnabled: "Key signed late",
  followUp: "Follow-up",
  cleanup: "+24 h cleanup",
  manual: "Owner Move (forward key)",
  move: "Owner Move (one-shot key)",
  operator: "Operator Forward now",
};

/** The app's reading of a forward (R-b), for the console's outcome column. */
export function forwardOutcome(forward: Pick<ResidualForwardDoc, "state" | "skipReason">): {
  label: string;
  color: MantineColor;
} {
  switch (forward.state) {
    case "pending":
    case "submitted":
      return { label: "Moving to the current wallet", color: "blue" };
    case "included":
      return { label: "Moved", color: "green" };
    case "failed":
      return { label: "Failed: Move to current wallet", color: "red" };
    case "skipped": {
      const reason = forward.skipReason
        ? (SKIP_REASON_LABELS[forward.skipReason] ?? forward.skipReason)
        : "Skipped";
      const moveOnly =
        forward.skipReason === "keyPending" ||
        forward.skipReason === "keyMissing" ||
        forward.skipReason === "keyExpired";
      return {
        label: moveOnly ? `${reason}: Move only` : reason,
        color:
          forward.skipReason === "destinationUnavailable" ? "red" : moveOnly ? "orange" : "gray",
      };
    }
    default:
      return { label: forward.state, color: "gray" };
  }
}

type ForwardLike = Pick<
  ResidualForwardDoc,
  "wallet" | "role" | "state" | "skipReason" | "amountMicroUsdc" | "createdAt" | "updatedAt"
> &
  Partial<Pick<ResidualForwardDoc, "id" | "failureReason">>;

/** The newest forward of each role (by `createdAt`), as the app maps a previous wallet's row. */
export function latestForwardByRole<T extends ForwardLike>(
  forwards: readonly T[],
): Partial<Record<AccountRole, T>> {
  const out: Partial<Record<AccountRole, T>> = {};
  for (const forward of forwards) {
    const role = forward.role === "reserve" ? "reserve" : forward.role === "main" ? "main" : null;
    if (!role) continue;
    const current = out[role];
    if (!current || (ms(forward.createdAt) ?? 0) > (ms(current.createdAt) ?? 0))
      out[role] = forward;
  }
  return out;
}

function isLiveForward(forward: Pick<ResidualForwardDoc, "state">): boolean {
  return forward.state === "pending" || forward.state === "submitted";
}

/**
 * The paged `residual_forward_failed` / `residual_forward_security` markers that Firestore lets the
 * console see for one previous wallet (`forwardFailed`, `overdue`, `destinationUnavailable`,
 * `deletedWalletCredit`, `forwardKeyOnOperableWallet`). `armMismatch` (a permission read back from
 * chain unlike the one minted) exists only in the logs.
 */
export function residualAlerts(input: {
  wallet: Pick<WithDocId<WalletDoc>, "_docId" | "state">;
  /** The wallet is closed (soft-deleted): its inventory's `walletDeletedAt`. */
  deleted: boolean;
  balances: { main: number | null; reserve: number | null };
  forwards: readonly ForwardLike[];
  keys: readonly KeyLike[];
  migrations: readonly MigrationLike[];
  now: number;
}): AlertMarker[] {
  const { now } = input;
  const markers: AlertMarker[] = [];
  const latest = latestForwardByRole(
    input.forwards.filter((f) => f.wallet === input.wallet._docId),
  );
  for (const role of ACCOUNT_ROLES) {
    const forward = latest[role];
    if (forward?.state === "failed") {
      markers.push({
        kind: "forwardFailed",
        label: `${role === "main" ? "Main" : "Reserve"} forward failed`,
        color: "red",
        detail: `The latest ${role} forward failed${forward.failureReason ? ` (${forward.failureReason})` : ""}. The next credit forwards again; Forward now retries while a forward key is live, else the owner's Move.`,
      });
    }
    if (forward?.state === "skipped" && forward.skipReason === "destinationUnavailable") {
      markers.push({
        kind: "destinationUnavailable",
        label: `${role === "main" ? "Main" : "Reserve"} recipient unavailable`,
        color: "red",
        detail:
          "The forward key's recipient is no longer an account of the owner: nothing is forwarded. Investigate the successor (paged ERROR).",
      });
    }
  }
  const overdue = input.forwards.filter(
    (f) =>
      f.wallet === input.wallet._docId &&
      isLiveForward(f) &&
      now - (ms(f.updatedAt) ?? now) >= RESIDUAL_FORWARD_OVERDUE_MS,
  );
  if (overdue.length > 0) {
    markers.push({
      kind: "overdue",
      label: "Forward overdue",
      color: "red",
      detail: `${overdue.length} forward(s) in flight for 30 minutes or more since their last change: the sweep re-submits a never-dispatched one and pages the rest. Check its smart-account operation.`,
    });
  }
  const balance = (input.balances.main ?? 0) + (input.balances.reserve ?? 0);
  if (input.deleted && balance > 0) {
    markers.push({
      kind: "deletedWalletCredit",
      label: "Closed wallet holds funds",
      color: "red",
      detail:
        "The wallet is closed but its accounts hold USDC: no key may sign for it, so nothing forwards. An operator must recover the funds.",
    });
  }
  const operable = forwardKeysOnOperableWallet(input);
  if (operable.length > 0) {
    markers.push({
      kind: "forwardKeyOnOperableWallet",
      label: "Forward key on an operable wallet",
      color: "red",
      detail: `${operable.length} forward key(s) still pending or enabled on a wallet that is not a previous wallet, or whose migration failed: the abandon should have revoked them. The daily expiry revokes them and pages CRITICAL.`,
    });
  }
  return markers;
}

// ───────────────────────────── operator commands ─────────────────────────────

/** `POST /admin/residualForwards` (Forward now): with a live forward key only, never mints a key. */
export function forwardNowAvailability(input: {
  wallet: Pick<WalletDoc, "state">;
  deleted: boolean;
  states: Record<AccountRole, ForwardKeyState>;
}): CommandAvailability & { role?: AccountRole } {
  if (!isPreviousWallet(input.wallet))
    return { offered: false, note: `The wallet is ${input.wallet.state}, not a previous wallet.` };
  if (input.deleted)
    return {
      offered: false,
      note: "The wallet is closed: no key may sign for it. Recover its funds by hand.",
    };
  const live = liveForwardRoles(input.states);
  if (live.length === 0)
    return {
      offered: false,
      note: `No live forward key (Main: ${input.states.main.label.toLowerCase()}, Reserve: ${input.states.reserve.label.toLowerCase()}): only the owner's one-shot Move can move this balance. Re-send the Move prompt.`,
    };
  const role = live.length === 1 ? live[0] : undefined;
  return {
    offered: true,
    role,
    note: `Forwards ${role ? `the ${role} account` : "both accounts"} to the successor with the live forward key: no owner signature. The backend reads the balance on chain; refused (409) when nothing is there or a forward is already in flight.`,
  };
}

/** `POST /admin/residualForwards/movePrompt`: a one-shot Move key for the owner to sign. */
export function movePromptAvailability(input: {
  wallet: Pick<WithDocId<WalletDoc>, "_docId" | "state" | "mainAddress" | "reserveAddress">;
  deleted: boolean;
  currentWalletId: string | null | undefined;
  balances: { main: number | null; reserve: number | null };
  states?: Record<AccountRole, ForwardKeyState>;
}): CommandAvailability {
  const { wallet } = input;
  if (!isPreviousWallet(wallet))
    return { offered: false, note: `The wallet is ${wallet.state}, not a previous wallet.` };
  if (input.deleted || !wallet.mainAddress || !wallet.reserveAddress)
    return {
      offered: false,
      note: "The wallet is closed or has no accounts: nothing can be signed for it.",
    };
  if (!input.currentWalletId || input.currentWalletId === wallet._docId)
    return { offered: false, note: "The owner has no current provisioned wallet to move to." };
  const total = (input.balances.main ?? 0) + (input.balances.reserve ?? 0);
  if (total <= 0)
    return {
      offered: false,
      note: "Nothing to move: walletAvailability shows 0 on both accounts.",
    };
  const live = input.states ? liveForwardRoles(input.states) : [];
  return {
    offered: true,
    note: `Mints a one-shot residual-{role}-move key for each account with a balance, pinned to the owner's current wallet and capped at the balance read now, and asks the owner to sign it in the app. Never re-prompts a standing forward key.${live.length > 0 ? " A forward key is live: Forward now needs no signature." : ""}`,
  };
}

// ───────────────────────────── fleet ─────────────────────────────

export interface ResidualWalletRow {
  id: string;
  wallet: WithDocId<WalletDoc>;
  /** The wallet's state, or `closed` when its inventory says it was soft-deleted. */
  state: string;
  deleted: boolean;
  owner: string;
  mainMicroUsdc: number;
  reserveMicroUsdc: number;
  totalMicroUsdc: number;
  /** When the balance last changed (the availability recompute): the residual sat at least since. */
  since: string | null;
  migration: WithDocId<WalletMigrationDoc> | null;
  successor: string | null;
  keys: Record<AccountRole, ForwardKeyState>;
  latest: Partial<Record<AccountRole, WithDocId<ResidualForwardDoc>>>;
  alerts: AlertMarker[];
}

function finite(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

/**
 * Previous wallets whose cached balance (Main + Reserve) is at or above the floor: the candidates
 * the page then joins forwards, migrations and keys for.
 */
export function residualCandidates(
  wallets: readonly WithDocId<WalletDoc>[],
  availabilities: readonly WithDocId<WalletAvailabilityDoc>[],
  floor: number = RESIDUAL_FLOOR_MICRO_USDC,
): { wallet: WithDocId<WalletDoc>; availability: WithDocId<WalletAvailabilityDoc> }[] {
  const byId = new Map(availabilities.map((a) => [a._docId, a]));
  const out: { wallet: WithDocId<WalletDoc>; availability: WithDocId<WalletAvailabilityDoc> }[] =
    [];
  for (const wallet of wallets) {
    if (!isPreviousWallet(wallet)) continue;
    const availability = byId.get(wallet._docId);
    if (!availability) continue;
    const total =
      finite(availability.mainBalanceMicroUsdc) + finite(availability.reserveBalanceMicroUsdc);
    if (total >= floor) out.push({ wallet, availability });
  }
  return out;
}

/** Largest residual first, then the one sitting longest, then the id. */
export function sortResiduals(rows: readonly ResidualWalletRow[]): ResidualWalletRow[] {
  return [...rows].sort(
    (a, b) =>
      b.totalMicroUsdc - a.totalMicroUsdc ||
      (ms(a.since) ?? Infinity) - (ms(b.since) ?? Infinity) ||
      a.id.localeCompare(b.id),
  );
}

/** The Old-wallet residuals page's rows: candidates joined to their forward state, sorted. */
export function selectResidualWallets(input: {
  wallets: readonly WithDocId<WalletDoc>[];
  availabilities: readonly WithDocId<WalletAvailabilityDoc>[];
  migrations: readonly WithDocId<WalletMigrationDoc>[];
  keys: readonly KeyLike[];
  forwards: readonly WithDocId<ResidualForwardDoc>[];
  inventories: readonly Pick<WithDocId<WalletKeyInventoryDoc>, "_docId" | "walletDeletedAt">[];
  now: number;
  floor?: number;
}): ResidualWalletRow[] {
  const deletedIds = new Set(
    input.inventories.filter((i) => !!i.walletDeletedAt).map((i) => i._docId),
  );
  const rows = residualCandidates(input.wallets, input.availabilities, input.floor).map(
    ({ wallet, availability }): ResidualWalletRow => {
      const migration = migrationOfSource(input.migrations, wallet._docId);
      const deleted = deletedIds.has(wallet._docId);
      const main = finite(availability.mainBalanceMicroUsdc);
      const reserve = finite(availability.reserveBalanceMicroUsdc);
      const forwards = input.forwards.filter((f) => f.wallet === wallet._docId);
      return {
        id: wallet._docId,
        wallet,
        state: deleted ? "closed" : wallet.state,
        deleted,
        owner: wallet.owner,
        mainMicroUsdc: main,
        reserveMicroUsdc: reserve,
        totalMicroUsdc: main + reserve,
        since: availability.computedAt ?? availability.updatedAt ?? null,
        migration,
        successor: wallet.migratedToWallet ?? migration?.newWallet ?? null,
        keys: forwardKeyStates({ wallet, migration, keys: input.keys, now: input.now }),
        latest: latestForwardByRole(forwards),
        alerts: residualAlerts({
          wallet,
          deleted,
          balances: { main, reserve },
          forwards,
          keys: input.keys,
          migrations: input.migrations,
          now: input.now,
        }),
      };
    },
  );
  return sortResiduals(rows);
}
